import {
  createRequestTransport,
  type RequestTransport,
} from '@hurdl/http-client';

/** What the composing application supplies to reach the review surface. */
export interface MenusClientConfiguration {
  /**
   * Where the review router is mounted, for example
   * `/products/uk-fast-food-menus/bespoke/menu-review`.
   *
   * The base address is the application's decision — the Admin_Panel mounts the
   * Bespoke_Page router at a product-scoped path — and this client holds none of its
   * own, so no address literal reaches the composing frontend.
   */
  readonly baseUrl: string;

  /**
   * A function performing an authenticated request.
   *
   * The Admin_Panel composes this client behind its own authorisation, so the
   * credential — and whether one travels at all — is the supplied function's business,
   * not this client's. Absent, requests go through `globalThis.fetch` with no
   * credential of any kind, which is what a test and a local, unauthenticated harness
   * substitute.
   */
  readonly authorisedFetch?: typeof globalThis.fetch;
}

/**
 * Builds the transport every generated operation reaches through the mutator in
 * `request.ts`.
 *
 * The one argument is the whole of what this application contributes: the address
 * resolution, the request, the envelope read and the error map are
 * `@hurdl/http-client`'s. It passes no `credentials` — the review surface holds no
 * cookie of its own — spreading the request function in only where the caller named
 * one.
 */
export function createMenusTransport(
  configuration: MenusClientConfiguration,
): RequestTransport {
  return createRequestTransport({
    baseUrl: configuration.baseUrl,
    ...(configuration.authorisedFetch === undefined
      ? {}
      : { send: configuration.authorisedFetch }),
  });
}
